import { useState, useEffect, useRef, useMemo } from 'react';
import { Modal } from '../shared/Modal';
import { RosterPasteInput } from './RosterPasteInput';
import { RosterResults } from './RosterResults';
import { FlightMatchStep } from './FlightMatchStep';
import { ManualFlightForm } from './ManualFlightForm';
import { CalendarImportView } from './CalendarImportView';
import { ImportGlobe } from './ImportGlobe';
import { useFlightEntry } from '../../hooks/useFlightEntry';
import { useRosterImport, type SaveResult } from '../../hooks/useRosterImport';
import { useApp } from '../../context/AppContext';
import { incrementRosterImportCount } from '../../utils/premiumGating';
import { PremiumUpsellSheet } from '../premium/PremiumUpsellSheet';
import type { Flight } from '../../types';

type EntryMethod = 'roster' | 'manual' | 'calendar';

interface FlightEntryModalProps {
  open: boolean;
  onClose: () => void;
  editFlight?: Flight;
  calendarMonth?: number;
  calendarYear?: number;
}

export function FlightEntryModal({ open, onClose, editFlight, calendarMonth, calendarYear }: FlightEntryModalProps) {
  const entry = useFlightEntry();
  const roster = useRosterImport(calendarMonth, calendarYear);
  const { currentUser, pendingSharedText, pendingSharedImage, clearPendingShare } = useApp();
  const [method, setMethod] = useState<EntryMethod>('roster');
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [showUpsell, setShowUpsell] = useState(false);
  const hasHandledShare = useRef(false);

  // When editing a flight, go straight to manual form pre-populated
  useEffect(() => {
    if (open && editFlight) {
      entry.setFlightNumber(editFlight.flightNumber);
      entry.setDate(editFlight.date);
      entry.setManualDep(editFlight.departureAirport);
      entry.setManualArr(editFlight.arrivalAirport);
      entry.setManualDepTime(editFlight.departureTime ?? '');
      entry.setManualArrTime(editFlight.arrivalTime ?? '');
      entry.setAircraftType(editFlight.aircraftType ?? '');
      setMethod('manual');
    }
  }, [open, editFlight]); // eslint-disable-line react-hooks/exhaustive-deps

  // Consume pending shared data from context (set by share extension)
  // This avoids the race condition where the DOM event fires before this modal mounts
  useEffect(() => {
    if (!open) {
      hasHandledShare.current = false;
      return;
    }
    if (hasHandledShare.current) return;

    if (pendingSharedText) {
      hasHandledShare.current = true;
      roster.setRosterText(pendingSharedText);
      setMethod('roster');
      clearPendingShare();
    } else if (pendingSharedImage) {
      hasHandledShare.current = true;
      roster.addSharedImage(pendingSharedImage);
      setMethod('roster');
      clearPendingShare();
    }
  }, [open, pendingSharedText, pendingSharedImage, clearPendingShare, roster]);

  const handleClose = () => {
    entry.reset();
    roster.reset();
    setMethod('roster');
    setImportMessage(null);
    onClose();
  };

  const isEditing = !!editFlight;

  const title = isEditing
    ? 'Edit Flight'
    : {
        roster: roster.step === 'results'
          ? 'Review Import'
          : roster.step === 'matching'
            ? 'Match Flights'
            : roster.step === 'animating'
              ? 'Importing...'
              : 'Add Flights',
        manual: 'Manual Entry',
        calendar: 'Calendar Import',
      }[method];

  const effectiveTitle = importMessage ? 'Import Complete' : title;

  return (
    <Modal open={open} onClose={handleClose} title={effectiveTitle}>
      {/* Calendar import flow */}
      {method === 'calendar' && (
        <CalendarImportView
          onClose={handleClose}
          onBack={() => setMethod('roster')}
        />
      )}

      {/* Roster import flow */}
      {method === 'roster' && roster.step === 'input' && (
        <RosterPasteInput
          rosterText={roster.rosterText}
          setRosterText={roster.setRosterText}
          images={roster.images}
          onAddImage={roster.addImage}
          onRemoveImage={roster.removeImage}
          onAddCameraImage={roster.addCameraImage}
          onSubmit={roster.submitRoster}
          onCancel={handleClose}
          parsing={roster.parsing}
          error={roster.parseError}
          userAirline={currentUser?.airlineCode}
          userBase={currentUser?.baseAirport}
          userId={currentUser?.id}
          rosterMonth={roster.rosterMonth}
          rosterYear={roster.rosterYear}
          onMonthChange={(m, y) => {
            roster.setRosterMonth(m);
            roster.setRosterYear(y);
          }}
          onManualEntry={() => setMethod('manual')}
          onCalendarImport={() => setMethod('calendar')}
          initialMode={
            pendingSharedImage ? 'photo' :
            pendingSharedText ? 'text' :
            undefined
          }
        />
      )}

      {/* Globe animation during import */}
      {method === 'roster' && roster.step === 'animating' && (
        <ImportGlobeWrapper
          parsedEntries={roster.parsedEntries}
          baseAirport={currentUser?.baseAirport || 'DXB'}
          isLoading={roster.parsing}
          onComplete={roster.completeAnimation}
        />
      )}

      {method === 'roster' && roster.step === 'matching' && (
        <FlightMatchStep
          entries={roster.parsedEntries}
          candidates={roster.matchCandidates}
          selections={roster.matchSelections}
          loading={roster.loadingCandidates}
          onSelect={roster.selectCandidate}
          onConfirm={roster.confirmMatches}
          onBack={() => {
            roster.reset();
          }}
        />
      )}

      {/* Roster results — full review with per-flight editing */}
      {method === 'roster' && roster.step === 'results' && !importMessage && (
        <RosterResults
          entries={roster.parsedEntries}
          selectedIndices={roster.selectedIndices}
          selectedFlightCount={roster.selectedFlightCount}
          onToggle={roster.toggleEntry}
          onSelectAll={roster.selectAllFlights}
          onDeselectAll={roster.deselectAll}
          onUpdateEntry={roster.updateEntry}
          onRemoveEntry={roster.removeEntry}
          onSave={async () => {
            const result = await roster.saveSelected();
            if (result.insertedCount > 0) incrementRosterImportCount();

            if (result.duplicatesSkipped > 0) {
              const msg = buildImportMessage(result);
              setImportMessage(msg);
            } else {
              handleClose();
            }
          }}
          onCancel={() => {
            roster.reset();
          }}
          saving={roster.saving}
        />
      )}

      {method === 'roster' && importMessage && (
        <div className="flex flex-col items-center gap-4 py-4">
          <p className="text-sm text-slate-200 text-center whitespace-pre-line">{importMessage}</p>
          <button
            onClick={handleClose}
            className="px-6 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent-dark"
          >
            OK
          </button>
        </div>
      )}

      <PremiumUpsellSheet open={showUpsell} onClose={() => setShowUpsell(false)} />

      {/* Manual entry (or edit mode) */}
      {method === 'manual' && (
        <ManualFlightForm
          flightNumber={entry.flightNumber}
          setFlightNumber={entry.setFlightNumber}
          date={entry.date}
          setDate={entry.setDate}
          dep={entry.manualDep}
          setDep={entry.setManualDep}
          arr={entry.manualArr}
          setArr={entry.setManualArr}
          depTime={entry.manualDepTime}
          setDepTime={entry.setManualDepTime}
          arrTime={entry.manualArrTime}
          setArrTime={entry.setManualArrTime}
          aircraftType={entry.aircraftType}
          setAircraftType={entry.setAircraftType}
          onSave={() => {
            const ok = isEditing
              ? entry.saveEdit(editFlight!.id)
              : entry.saveManual();
            if (ok) handleClose();
          }}
          onCancel={() => {
            if (isEditing) {
              handleClose();
            } else {
              entry.reset();
              setMethod('roster');
            }
          }}
          error={entry.error}
          saveLabel={isEditing ? 'Save Changes' : 'Save Flight'}
        />
      )}
    </Modal>
  );
}

/** Memoizes the flights array so ImportGlobe doesn't re-animate on parent re-renders */
function ImportGlobeWrapper({
  parsedEntries,
  baseAirport,
  isLoading,
  onComplete,
}: {
  parsedEntries: import('../../types').ParsedRosterEntry[];
  baseAirport: string;
  isLoading: boolean;
  onComplete: () => void;
}) {
  const flights = useMemo(
    () =>
      parsedEntries
        .filter((e) => e.type === 'flight' && e.departureAirport && e.arrivalAirport)
        .map((e) => ({
          dep: e.departureAirport!,
          arr: e.arrivalAirport!,
          flightNumber: e.flightNumber,
        })),
    [parsedEntries],
  );
  return (
    <ImportGlobe
      flights={flights}
      baseAirport={baseAirport}
      isLoading={isLoading}
      onComplete={onComplete}
    />
  );
}

function buildImportMessage(result: SaveResult): string {
  const { insertedCount, duplicatesSkipped } = result;
  const parts: string[] = [];

  if (insertedCount > 0) {
    parts.push(`${insertedCount} flight${insertedCount !== 1 ? 's' : ''} imported.`);
  }

  if (duplicatesSkipped > 0) {
    parts.push(
      `${duplicatesSkipped} duplicate${duplicatesSkipped !== 1 ? 's' : ''} skipped (already in your schedule).`
    );
  }

  if (insertedCount === 0 && duplicatesSkipped > 0) {
    return `All ${duplicatesSkipped} flight${duplicatesSkipped !== 1 ? 's were' : ' was'} already in your schedule. Nothing new to import.`;
  }

  return parts.join('\n');
}
